import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { useBatteryStore } from '../../store/batteryStore';

// Company logo URL for battery branding (hosted locally to avoid CORS issues)
const LOGO_URL = '/assets/voltaplex-logo.png';

// Texture loader instance (singleton)
let logoTexture = null;
let logoLoadPromise = null;

// Load the logo texture once
const loadLogoTexture = () => {
  if (logoTexture) return Promise.resolve(logoTexture);
  if (logoLoadPromise) return logoLoadPromise;
  
  logoLoadPromise = new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      LOGO_URL,
      (texture) => {
        texture.colorSpace = THREE.SRGBColorSpace;
        logoTexture = texture;
        resolve(texture);
      },
      undefined,
      (error) => {
        console.warn('Failed to load logo texture:', error);
        resolve(null);
      }
    );
  });
  
  return logoLoadPromise;
};

// Reactive Wave Background Component
const ReactiveWaveBackground = ({ mousePosition, isInteracting }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const timeRef = useRef(0);
  const targetMouseRef = useRef({ x: 0.5, y: 0.5 });
  const currentMouseRef = useRef({ x: 0.5, y: 0.5 });
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);
    
    const animate = () => {
      const width = canvas.offsetWidth;
      const height = canvas.offsetHeight;
      
      timeRef.current += 0.008;
      
      // Smooth mouse following
      currentMouseRef.current.x += (targetMouseRef.current.x - currentMouseRef.current.x) * 0.05;
      currentMouseRef.current.y += (targetMouseRef.current.y - currentMouseRef.current.y) * 0.05;
      
      const mx = currentMouseRef.current.x;
      const my = currentMouseRef.current.y;
      
      // Clear with white
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Draw multiple wave layers
      const layers = [
        { color: 'rgba(0, 0, 0, 0.03)', amplitude: 60, frequency: 0.008, speed: 1, offset: 0 },
        { color: 'rgba(0, 0, 0, 0.04)', amplitude: 45, frequency: 0.012, speed: 1.2, offset: 100 },
        { color: 'rgba(0, 0, 0, 0.05)', amplitude: 35, frequency: 0.015, speed: 0.8, offset: 200 },
        { color: 'rgba(0, 200, 255, 0.03)', amplitude: 50, frequency: 0.01, speed: 1.5, offset: 150 },
      ];
      
      layers.forEach((layer, index) => {
        ctx.beginPath();
        ctx.moveTo(0, height);
        
        // Mouse influence on wave
        const mouseInfluence = isInteracting ? 2.5 : 1;
        const mouseOffsetX = (mx - 0.5) * 100 * mouseInfluence;
        const mouseOffsetY = (my - 0.5) * 50 * mouseInfluence;
        
        for (let x = 0; x <= width; x += 5) {
          const normalizedX = x / width;
          const distanceFromMouse = Math.abs(normalizedX - mx);
          const mouseWave = Math.exp(-distanceFromMouse * 3) * mouseOffsetY * (isInteracting ? 1.5 : 0.5);
          
          const y = height * 0.5 + layer.offset +
            Math.sin(x * layer.frequency + timeRef.current * layer.speed + mouseOffsetX * 0.01) * layer.amplitude * mouseInfluence +
            Math.sin(x * layer.frequency * 1.5 + timeRef.current * layer.speed * 0.7) * layer.amplitude * 0.5 +
            mouseWave;
          
          ctx.lineTo(x, y);
        }
        
        ctx.lineTo(width, height);
        ctx.closePath();
        
        // Create gradient for each wave
        const gradient = ctx.createLinearGradient(0, height * 0.3, 0, height);
        const baseOpacity = isInteracting ? 0.08 : 0.05;
        gradient.addColorStop(0, `rgba(0, 0, 0, ${baseOpacity * (index + 1) * 0.5})`);
        gradient.addColorStop(1, `rgba(0, 0, 0, ${baseOpacity * (index + 1)})`);
        
        ctx.fillStyle = layer.color;
        ctx.fill();
      });
      
      // Add subtle radial gradient following mouse
      const radialGradient = ctx.createRadialGradient(
        mx * width, my * height, 0,
        mx * width, my * height, 300
      );
      radialGradient.addColorStop(0, isInteracting ? 'rgba(0, 200, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)');
      radialGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = radialGradient;
      ctx.fillRect(0, 0, width, height);
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isInteracting]);
  
  // Update target mouse position
  useEffect(() => {
    targetMouseRef.current = mousePosition;
  }, [mousePosition]);
  
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ background: '#ffffff' }}
    />
  );
};

// Cell color mappings based on real-world battery wrappers
const CELL_COLORS = {
  '18650': {
    primary: 0xcc2222,      // Red - generic 18650
    highlight: 0xff6666,
    dark: 0x881111,
    name: 'Red'
  },
  '18650-HG2': {
    primary: 0x8B4513,      // Brown - LG HG2 (chocolate brown wrapper)
    highlight: 0xA0522D,
    dark: 0x5D2E0C,
    name: 'Brown'
  },
  '21700': {
    primary: 0x2266cc,      // Blue - generic 21700
    highlight: 0x4488ee,
    dark: 0x114488,
    name: 'Blue'
  },
  '21700-50E': {
    primary: 0x008080,      // Teal - Samsung 50E (cyan/teal wrapper)
    highlight: 0x20B2AA,
    dark: 0x005555,
    name: 'Teal'
  },
  '26650': {
    primary: 0x228B22,      // Green - LiFePO4 cells
    highlight: 0x32CD32,
    dark: 0x155215,
    name: 'Green'
  },
  '32650': {
    primary: 0x006400,      // Dark Green - Large LiFePO4
    highlight: 0x228B22,
    dark: 0x003300,
    name: 'Dark Green'
  },
  'default': {
    primary: 0xcc2222,
    highlight: 0xff6666,
    dark: 0x881111,
    name: 'Red'
  }
};

// Get color scheme for a cell type
const getCellColors = (cellId) => {
  return CELL_COLORS[cellId] || CELL_COLORS['default'];
};

// Create a realistic 3D battery cell mesh
const createBatteryMesh = (diameter, height, cellId = 'default', logoTex = null) => {
  const group = new THREE.Group();
  const radius = diameter / 2;
  const colors = getCellColors(cellId);
  
  // Main cylinder body (colored wrapper)
  const bodyGeometry = new THREE.CylinderGeometry(radius, radius, height * 0.92, 32);
  const bodyMaterial = new THREE.MeshPhysicalMaterial({
    color: colors.primary,
    metalness: 0.3,
    roughness: 0.4,
    clearcoat: 0.3,
    clearcoatRoughness: 0.2,
  });
  const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
  body.position.y = height * 0.04;
  group.add(body);
  
  // Add logo decal on the side of the battery if texture is loaded
  if (logoTex) {
    // Create a plane for the logo - sized to fit on the battery body
    // Logo should be smaller than the cylinder diameter to look realistic
    const logoHeight = height * 0.2; // Logo takes up ~20% of cell height
    const logoWidth = logoHeight * 1.2; // Slightly wider aspect ratio
    
    // Ensure logo doesn't exceed the cylinder's visible surface
    const maxLogoWidth = radius * 1.4; // Max width is slightly less than diameter
    const finalLogoWidth = Math.min(logoWidth, maxLogoWidth);
    const finalLogoHeight = finalLogoWidth / 1.2; // Maintain aspect ratio
    
    const logoGeometry = new THREE.PlaneGeometry(finalLogoWidth, finalLogoHeight);
    const logoMaterial = new THREE.MeshBasicMaterial({
      map: logoTex,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: true,
      depthTest: true,
    });
    
    // Position logos at 4 cardinal directions around the cylinder for visibility from any angle
    const positions = [
      { pos: [0, height * 0.04, radius + 0.15], rot: 0 },          // Front
      { pos: [0, height * 0.04, -(radius + 0.15)], rot: Math.PI }, // Back
      { pos: [radius + 0.15, height * 0.04, 0], rot: Math.PI / 2 }, // Right
      { pos: [-(radius + 0.15), height * 0.04, 0], rot: -Math.PI / 2 }, // Left
    ];
    
    positions.forEach(({ pos, rot }) => {
      const logoMesh = new THREE.Mesh(logoGeometry, logoMaterial.clone());
      logoMesh.position.set(pos[0], pos[1], pos[2]);
      logoMesh.rotation.y = rot;
      group.add(logoMesh);
    });
  }
  
  // Add gradient effect with additional ring
  const gradientRingGeometry = new THREE.CylinderGeometry(radius * 1.001, radius * 1.001, height * 0.3, 32, 1, true);
  const gradientMaterial = new THREE.MeshBasicMaterial({
    color: colors.highlight,
    transparent: true,
    opacity: 0.15,
    side: THREE.DoubleSide,
  });
  const gradientRing = new THREE.Mesh(gradientRingGeometry, gradientMaterial);
  gradientRing.position.y = height * 0.15;
  group.add(gradientRing);
  
  // Negative terminal (bottom flat cap)
  const negativeCapGeometry = new THREE.CylinderGeometry(radius * 0.95, radius * 0.95, height * 0.02, 32);
  const negativeCapMaterial = new THREE.MeshStandardMaterial({
    color: 0x888888,
    metalness: 0.9,
    roughness: 0.2,
  });
  const negativeCap = new THREE.Mesh(negativeCapGeometry, negativeCapMaterial);
  negativeCap.position.y = -height * 0.45;
  group.add(negativeCap);
  
  // Positive terminal base (top silver ring)
  const positiveRingGeometry = new THREE.CylinderGeometry(radius * 0.9, radius * 0.9, height * 0.03, 32);
  const positiveRingMaterial = new THREE.MeshStandardMaterial({
    color: 0xaaaaaa,
    metalness: 0.9,
    roughness: 0.15,
  });
  const positiveRing = new THREE.Mesh(positiveRingGeometry, positiveRingMaterial);
  positiveRing.position.y = height * 0.47;
  group.add(positiveRing);
  
  // Positive terminal button (raised nub)
  const buttonGeometry = new THREE.CylinderGeometry(radius * 0.25, radius * 0.3, height * 0.04, 16);
  const buttonMaterial = new THREE.MeshStandardMaterial({
    color: 0xcccccc,
    metalness: 0.95,
    roughness: 0.1,
  });
  const button = new THREE.Mesh(buttonGeometry, buttonMaterial);
  button.position.y = height * 0.5;
  group.add(button);
  
  // Insulator ring (black ring around positive terminal)
  const insulatorGeometry = new THREE.TorusGeometry(radius * 0.7, radius * 0.08, 8, 32);
  const insulatorMaterial = new THREE.MeshStandardMaterial({
    color: 0x111111,
    metalness: 0.1,
    roughness: 0.8,
  });
  const insulator = new THREE.Mesh(insulatorGeometry, insulatorMaterial);
  insulator.rotation.x = Math.PI / 2;
  insulator.position.y = height * 0.46;
  group.add(insulator);
  
  // Add wrapper text/branding line
  const lineGeometry = new THREE.CylinderGeometry(radius * 1.002, radius * 1.002, height * 0.02, 32, 1, true);
  const lineMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.15,
  });
  const line = new THREE.Mesh(lineGeometry, lineMaterial);
  line.position.y = height * 0.1;
  group.add(line);
  
  return group;
};

// Create cell holder/spacer
const createHolderMesh = (cellDiameter, rows, cols) => {
  const group = new THREE.Group();
  const holderThickness = 3;
  const holeRadius = cellDiameter / 2 + 0.5;
  const spacing = cellDiameter + 2;
  
  const width = cols * spacing + 4;
  const depth = rows * spacing + 4;
  
  // Base plate with holes
  const shape = new THREE.Shape();
  shape.moveTo(-width/2, -depth/2);
  shape.lineTo(width/2, -depth/2);
  shape.lineTo(width/2, depth/2);
  shape.lineTo(-width/2, depth/2);
  shape.lineTo(-width/2, -depth/2);
  
  // Cut holes for cells
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const holeX = (c - (cols-1)/2) * spacing;
      const holeY = (r - (rows-1)/2) * spacing;
      const holePath = new THREE.Path();
      holePath.absarc(holeX, holeY, holeRadius, 0, Math.PI * 2, false);
      shape.holes.push(holePath);
    }
  }
  
  const extrudeSettings = {
    depth: holderThickness,
    bevelEnabled: false,
  };
  
  const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
  geometry.rotateX(-Math.PI / 2);
  
  const material = new THREE.MeshStandardMaterial({
    color: 0x2a2a2a,
    metalness: 0.1,
    roughness: 0.8,
  });
  
  const holder = new THREE.Mesh(geometry, material);
  group.add(holder);
  
  return group;
};

// Create nickel strip
const createNickelStrip = (length, width = 8, thickness = 0.2) => {
  const geometry = new THREE.BoxGeometry(width, thickness, length);
  const material = new THREE.MeshStandardMaterial({
    color: 0xc0c0c0,
    metalness: 0.95,
    roughness: 0.15,
  });
  return new THREE.Mesh(geometry, material);
};

// Create BMS board (Battery Management System)
const createBMSBoard = (packWidth, packDepth, bmsSpecs = null) => {
  const group = new THREE.Group();
  
  // BMS board dimensions - use specs if provided, otherwise estimate based on pack size
  const boardLength = bmsSpecs?.length_mm || Math.min(packWidth * 0.8, 65);
  const boardWidth = bmsSpecs?.width_mm || Math.min(packDepth * 0.6, 45);
  const boardThickness = 1.6; // Standard PCB thickness
  
  // Main PCB board (green)
  const pcbGeometry = new THREE.BoxGeometry(boardLength, boardThickness, boardWidth);
  const pcbMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a472a, // Dark green PCB color
    metalness: 0.1,
    roughness: 0.7,
  });
  const pcb = new THREE.Mesh(pcbGeometry, pcbMaterial);
  group.add(pcb);
  
  // Add solder mask layer (lighter green on top)
  const maskGeometry = new THREE.BoxGeometry(boardLength - 2, 0.1, boardWidth - 2);
  const maskMaterial = new THREE.MeshStandardMaterial({
    color: 0x228B22, // Forest green
    metalness: 0.05,
    roughness: 0.6,
  });
  const mask = new THREE.Mesh(maskGeometry, maskMaterial);
  mask.position.y = boardThickness / 2 + 0.05;
  group.add(mask);
  
  // Add MOSFETs (black rectangular components) - typically 2-4 for discharge/charge
  const mosfetGeometry = new THREE.BoxGeometry(8, 3, 10);
  const mosfetMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    metalness: 0.3,
    roughness: 0.4,
  });
  
  // Position MOSFETs on the board
  const mosfetPositions = [
    { x: -boardLength * 0.25, z: 0 },
    { x: boardLength * 0.25, z: 0 },
  ];
  
  mosfetPositions.forEach(pos => {
    const mosfet = new THREE.Mesh(mosfetGeometry, mosfetMaterial);
    mosfet.position.set(pos.x, boardThickness / 2 + 1.5, pos.z);
    group.add(mosfet);
  });
  
  // Add main IC chip (center)
  const icGeometry = new THREE.BoxGeometry(10, 2, 10);
  const icMaterial = new THREE.MeshStandardMaterial({
    color: 0x2a2a2a,
    metalness: 0.4,
    roughness: 0.3,
  });
  const ic = new THREE.Mesh(icGeometry, icMaterial);
  ic.position.set(0, boardThickness / 2 + 1, -boardWidth * 0.2);
  group.add(ic);
  
  // Add IC marking dot (white)
  const dotGeometry = new THREE.CircleGeometry(1, 16);
  const dotMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const dot = new THREE.Mesh(dotGeometry, dotMaterial);
  dot.rotation.x = -Math.PI / 2;
  dot.position.set(-3, boardThickness / 2 + 2.1, -boardWidth * 0.2 - 3);
  group.add(dot);
  
  // Add capacitors (small cylindrical components)
  const capGeometry = new THREE.CylinderGeometry(2, 2, 5, 12);
  const capMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    metalness: 0.2,
    roughness: 0.5,
  });
  
  const capPositions = [
    { x: boardLength * 0.35, z: boardWidth * 0.25 },
    { x: boardLength * 0.35, z: -boardWidth * 0.25 },
    { x: -boardLength * 0.35, z: boardWidth * 0.25 },
  ];
  
  capPositions.forEach(pos => {
    const cap = new THREE.Mesh(capGeometry, capMaterial);
    cap.position.set(pos.x, boardThickness / 2 + 2.5, pos.z);
    group.add(cap);
    
    // Add metallic top to capacitor
    const capTopGeometry = new THREE.CircleGeometry(2, 12);
    const capTopMaterial = new THREE.MeshStandardMaterial({
      color: 0x888888,
      metalness: 0.9,
      roughness: 0.2,
    });
    const capTop = new THREE.Mesh(capTopGeometry, capTopMaterial);
    capTop.rotation.x = -Math.PI / 2;
    capTop.position.set(pos.x, boardThickness / 2 + 5.1, pos.z);
    group.add(capTop);
  });
  
  // Add balance connector (white rectangular)
  const connectorGeometry = new THREE.BoxGeometry(15, 4, 5);
  const connectorMaterial = new THREE.MeshStandardMaterial({
    color: 0xf5f5f5,
    metalness: 0.1,
    roughness: 0.6,
  });
  const connector = new THREE.Mesh(connectorGeometry, connectorMaterial);
  connector.position.set(0, boardThickness / 2 + 2, boardWidth * 0.35);
  group.add(connector);
  
  // Add connector pins (gold colored)
  const pinMaterial = new THREE.MeshStandardMaterial({
    color: 0xFFD700,
    metalness: 0.9,
    roughness: 0.2,
  });
  
  for (let i = 0; i < 6; i++) {
    const pinGeometry = new THREE.BoxGeometry(1, 2, 0.5);
    const pin = new THREE.Mesh(pinGeometry, pinMaterial);
    pin.position.set(-6 + i * 2.4, boardThickness / 2 + 2, boardWidth * 0.35 + 2);
    group.add(pin);
  }
  
  // Add power wires attachment points (red and black pads)
  const padGeometry = new THREE.CylinderGeometry(3, 3, 0.5, 16);
  
  // Red pad (positive)
  const redPadMaterial = new THREE.MeshStandardMaterial({
    color: 0xcc0000,
    metalness: 0.8,
    roughness: 0.3,
  });
  const redPad = new THREE.Mesh(padGeometry, redPadMaterial);
  redPad.position.set(-boardLength * 0.4, boardThickness / 2 + 0.25, -boardWidth * 0.35);
  group.add(redPad);
  
  // Black pad (negative)
  const blackPadMaterial = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    metalness: 0.8,
    roughness: 0.3,
  });
  const blackPad = new THREE.Mesh(padGeometry, blackPadMaterial);
  blackPad.position.set(boardLength * 0.4, boardThickness / 2 + 0.25, -boardWidth * 0.35);
  group.add(blackPad);
  
  // Add some SMD resistors (tiny rectangles)
  const smdGeometry = new THREE.BoxGeometry(2, 0.5, 1);
  const smdMaterial = new THREE.MeshStandardMaterial({
    color: 0x2a2a2a,
    metalness: 0.2,
    roughness: 0.5,
  });
  
  for (let i = 0; i < 8; i++) {
    const smd = new THREE.Mesh(smdGeometry, smdMaterial);
    smd.position.set(
      -boardLength * 0.3 + (i % 4) * 5,
      boardThickness / 2 + 0.25,
      boardWidth * 0.15 + Math.floor(i / 4) * 4
    );
    group.add(smd);
  }
  
  return group;
};

// Create 3D text sprite for labels
const createTextSprite = (text, backgroundColor = 0xffdd00, textColor = '#000000') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 128;
  canvas.height = 64;
  
  // Background
  context.fillStyle = `#${backgroundColor.toString(16).padStart(6, '0')}`;
  context.fillRect(0, 0, canvas.width, canvas.height);
  
  // Border
  context.strokeStyle = '#000000';
  context.lineWidth = 3;
  context.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);
  
  // Text
  context.fillStyle = textColor;
  context.font = 'bold 36px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(text, canvas.width / 2, canvas.height / 2);
  
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(20, 10, 1);
  
  return sprite;
};

export const PackViewer3D = () => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const packGroupRef = useRef(null);
  const frameIdRef = useRef(null);
  const logoTextureRef = useRef(null);
  
  // Mouse tracking for reactive background
  const [mousePosition, setMousePosition] = useState({ x: 0.5, y: 0.5 });
  const [isInteracting, setIsInteracting] = useState(false);
  
  const { selectedResult, cells, selectedCellId, showGrid, showLabels, showAxes, includeBms, bmsModels, selectedBmsId } = useBatteryStore();
  
  const cell = cells.find(c => c.id === selectedCellId);
  const selectedBms = bmsModels.find(b => b.id === selectedBmsId);
  const cellDiameter = cell?.diameter || 18.6;
  const cellHeight = cell?.height || 65.2;
  const layout = selectedResult?.layout;
  const config = selectedResult?.config;
  const specs = selectedResult?.specs;
  
  // Load logo texture on mount
  useEffect(() => {
    loadLogoTexture().then((texture) => {
      logoTextureRef.current = texture;
    });
  }, []);
  
  // Mouse move handler for reactive background
  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMousePosition({ x, y });
  };
  
  const handleMouseDown = () => setIsInteracting(true);
  const handleMouseUp = () => setIsInteracting(false);
  const handleMouseLeave = () => {
    setIsInteracting(false);
    // Slowly return to center
    setMousePosition({ x: 0.5, y: 0.5 });
  };
  
  // Initialize Three.js scene
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    // Scene with transparent background (CSS handles the factory image)
    const scene = new THREE.Scene();
    // Background is transparent - CSS layer shows the blurred factory image
    sceneRef.current = scene;
    
    // Camera
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 10000);
    camera.position.set(200, 150, 200);
    cameraRef.current = camera;
    
    // Renderer with transparent background
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: true,
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setClearColor(0x000000, 0); // Fully transparent background
    renderer.shadowMap.enabled = false; // Disable shadows - no floor to catch them
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;
    
    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 50;
    controls.maxDistance = 1000;
    controls.target.set(0, 0, 0);
    controlsRef.current = controls;
    
    // Industrial Lighting Setup to match factory environment
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    // Primary overhead light (warm factory lighting)
    const mainLight = new THREE.DirectionalLight(0xfff5e6, 1.0);
    mainLight.position.set(50, 300, 50);
    scene.add(mainLight);
    
    // Fill light (cool tone for contrast)
    const fillLight = new THREE.DirectionalLight(0x88aacc, 0.3);
    fillLight.position.set(-150, 100, -100);
    scene.add(fillLight);
    
    // Rim light for edge definition
    const rimLight = new THREE.DirectionalLight(0xffffff, 0.2);
    rimLight.position.set(0, 50, 150);
    scene.add(rimLight);
    
    // Pack group
    const packGroup = new THREE.Group();
    scene.add(packGroup);
    packGroupRef.current = packGroup;
    
    // Animation loop
    const animate = () => {
      frameIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();
    
    // Handle resize
    const handleResize = () => {
      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameIdRef.current);
      controls.dispose();
      renderer.dispose();
      container.removeChild(renderer.domElement);
    };
  }, []);
  
  // Grid and axes are disabled - clean background only
  // (Keeping the useEffect structure for potential future use)
  useEffect(() => {
    // Grid disabled - no longer adding GridHelper
  }, [showGrid]);
  
  // Update axes
  useEffect(() => {
    // Axes disabled - no longer adding AxesHelper
  }, [showAxes]);
  
  // Update pack when layout changes
  useEffect(() => {
    if (!packGroupRef.current || !sceneRef.current) return;
    
    const packGroup = packGroupRef.current;
    
    // Clear existing pack
    while (packGroup.children.length > 0) {
      const child = packGroup.children[0];
      packGroup.remove(child);
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) {
          child.material.forEach(m => m.dispose());
        } else {
          child.material.dispose();
        }
      }
    }
    
    if (!layout || !config || !layout.cells || layout.cells.length === 0) {
      return;
    }
    
    const series = config.series;
    const parallel = config.parallel;
    const layers = config.layers || 1;
    const spacing = cellDiameter + 2;
    const layerHeight = cellHeight + 8; // Height per layer including holder
    
    // Calculate cells per layer
    const cellsPerLayer = Math.ceil((series * parallel) / layers);
    const colsPerLayer = series;
    const rowsPerLayer = Math.ceil(parallel / layers);
    
    // Function to build the pack (called after texture is ready or immediately if not available)
    const buildPack = (logoTex) => {
      // Create battery template with correct color for cell type and logo
      const batteryTemplate = createBatteryMesh(cellDiameter, cellHeight, selectedCellId, logoTex);
      
      // Position batteries in layers
      for (let layer = 0; layer < layers; layer++) {
        const layerY = layer * layerHeight;
        const rowsInThisLayer = Math.min(rowsPerLayer, parallel - layer * rowsPerLayer);
        
        if (rowsInThisLayer <= 0) continue;
        
        for (let s = 0; s < series; s++) {
          for (let r = 0; r < rowsInThisLayer; r++) {
            // Clone the template (need to create new meshes for logo to show properly)
            const battery = createBatteryMesh(cellDiameter, cellHeight, selectedCellId, logoTex);
            
            // Position in grid layout
            const x = (s - (series - 1) / 2) * spacing;
            const z = (r - (rowsInThisLayer - 1) / 2) * spacing;
            const y = layerY + cellHeight / 2;
            
            battery.position.set(x, y, z);
            battery.castShadow = true;
            battery.receiveShadow = true;
            packGroup.add(battery);
          }
        }
        
        // Add top holder for this layer
        const topHolder = createHolderMesh(cellDiameter, rowsInThisLayer, series);
        topHolder.position.y = layerY + cellHeight + 2;
        packGroup.add(topHolder);
        
        // Add bottom holder for this layer
        const bottomHolder = createHolderMesh(cellDiameter, rowsInThisLayer, series);
        bottomHolder.position.y = layerY - 2;
        packGroup.add(bottomHolder);
        
        // Add nickel strips on top (parallel connections)
        for (let s = 0; s < series; s++) {
          if (rowsInThisLayer > 1) {
            const stripLength = (rowsInThisLayer - 1) * spacing;
            const strip = createNickelStrip(stripLength);
            strip.position.set(
              (s - (series - 1) / 2) * spacing,
              layerY + cellHeight + 4,
              0
            );
            packGroup.add(strip);
          }
        }
        
        // Add series connection strips for this layer
        for (let s = 0; s < series - 1; s++) {
          const strip = createNickelStrip(spacing * 0.8, 6, 0.15);
          strip.rotation.y = Math.PI / 2;
          const yPos = (s % 2 === 0) ? layerY - 3 : layerY + cellHeight + 4;
          const zPos = (s % 2 === 0) ? -(rowsInThisLayer - 1) / 2 * spacing : (rowsInThisLayer - 1) / 2 * spacing;
          strip.position.set(
            (s - (series - 2) / 2) * spacing + spacing / 2,
            yPos,
            zPos
          );
          packGroup.add(strip);
        }
        
        // Add layer label
        if (layers > 1) {
          const layerLabel = createTextSprite(`L${layer + 1}`, 0x333333, '#ffffff');
          layerLabel.position.set(
            -((series - 1) / 2 + 1.5) * spacing,
            layerY + cellHeight / 2,
            0
          );
          layerLabel.scale.set(15, 7.5, 1);
          packGroup.add(layerLabel);
        }
      }
      
      // Add inter-layer connections if multiple layers
      if (layers > 1) {
        for (let layer = 0; layer < layers - 1; layer++) {
          const connectorHeight = 8;
          const connectorGeometry = new THREE.CylinderGeometry(2, 2, connectorHeight, 8);
          const connectorMaterial = new THREE.MeshStandardMaterial({
            color: 0xffaa00,
            metalness: 0.8,
            roughness: 0.2,
          });
          
          // Connect layers at the ends
          const connector1 = new THREE.Mesh(connectorGeometry, connectorMaterial);
          connector1.position.set(
            (series - 1) / 2 * spacing,
            layer * layerHeight + cellHeight + connectorHeight / 2 + 2,
            0
          );
          packGroup.add(connector1);
          
          const connector2 = new THREE.Mesh(connectorGeometry, connectorMaterial);
          connector2.position.set(
            -(series - 1) / 2 * spacing,
            layer * layerHeight + cellHeight + connectorHeight / 2 + 2,
            0
          );
          packGroup.add(connector2);
        }
      }
      
      // Add labels
      if (showLabels) {
        // Series labels on bottom layer
        for (let s = 0; s < series; s += 2) {
          const label = createTextSprite((s + 1).toString());
          label.position.set(
            (s - (series - 1) / 2) * spacing,
            -15,
            0
          );
          packGroup.add(label);
        }
        
        // B+ label
        const bPlusLabel = createTextSprite('B+');
        bPlusLabel.position.set(
          ((series - 1) / 2 + 1.5) * spacing,
          (layers - 1) * layerHeight + cellHeight / 2,
          0
        );
        packGroup.add(bPlusLabel);
        
        // B- label
        const bMinusLabel = createTextSprite('B-');
        bMinusLabel.position.set(
          (-(series - 1) / 2 - 1.5) * spacing,
          cellHeight / 2,
          0
        );
        packGroup.add(bMinusLabel);
      }
      
      // Add BMS board if included in config
      if (specs?.bms_included) {
        const packWidth = series * spacing;
        const packDepth = rowsPerLayer * spacing;
        const bmsBoard = createBMSBoard(packWidth, packDepth, specs.bms_dimensions_mm);
        
        // Position BMS on top of the pack
        const topLayerY = (layers - 1) * layerHeight;
        bmsBoard.position.set(
          0,
          topLayerY + cellHeight + 10, // Above top holder with some clearance
          0
        );
        packGroup.add(bmsBoard);
        
        // Add BMS label
        if (showLabels) {
          const bmsLabel = createTextSprite('BMS', 0x228B22, '#ffffff');
          bmsLabel.position.set(
            0,
            topLayerY + cellHeight + 20,
            0
          );
          bmsLabel.scale.set(15, 7.5, 1);
          packGroup.add(bmsLabel);
        }
      }
      
      // Update camera to frame the pack
      if (cameraRef.current && controlsRef.current) {
        const packWidth = series * spacing;
        const packDepth = Math.ceil(parallel / layers) * spacing;
        const packHeight = layers * layerHeight;
        const packSize = Math.max(packWidth, packDepth, packHeight);
        const distance = packSize * 2;
        cameraRef.current.position.set(distance * 0.8, distance * 0.6, distance * 0.8);
        controlsRef.current.target.set(0, packHeight / 2, 0);
        controlsRef.current.update();
      }
    };
    
    // Load logo texture and then build pack
    loadLogoTexture().then((logoTex) => {
      logoTextureRef.current = logoTex;
      buildPack(logoTex);
    });
    
  }, [layout, config, specs, cellDiameter, cellHeight, showLabels, selectedCellId]);
  
  return (
    <div 
      className="w-full h-full relative overflow-hidden" 
      data-testid="pack-viewer-3d"
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Reactive wave background */}
      <ReactiveWaveBackground mousePosition={mousePosition} isInteracting={isInteracting} />
      
      {/* Three.js canvas container */}
      <div ref={containerRef} className="absolute inset-0" />
      
      {/* Overlay for empty state */}
      {(!layout || !layout.cells || layout.cells.length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-zinc-500">
            <div className="font-heading text-xl mb-2">NO PACK SELECTED</div>
            <div className="text-sm">Configure parameters and generate packs</div>
          </div>
        </div>
      )}
      
      {/* Controls hint */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-zinc-500 font-mono bg-white/80 px-3 py-1.5 rounded-full border border-zinc-200 shadow-sm">
        Left-click drag to rotate • Right-click drag to pan • Scroll to zoom
      </div>
      
      {/* Specs overlay */}
      {selectedResult?.specs && (
        <div className="absolute top-4 right-4 bg-white/90 border border-zinc-200 rounded-lg p-3 backdrop-blur-sm shadow-lg">
          <div className="text-cyan-600 font-mono text-sm">
            {config?.series}S{config?.parallel}P
          </div>
          <div className="text-blue-600 font-mono text-lg font-bold">
            {selectedResult.specs.nominal_voltage.toFixed(1)}V
          </div>
          <div className="text-cyan-600 font-mono text-lg">
            {selectedResult.specs.capacity_wh.toFixed(0)}Wh
          </div>
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-zinc-200">
            <div 
              className="w-4 h-4 rounded-full border border-zinc-300" 
              style={{ backgroundColor: `#${getCellColors(selectedCellId).primary.toString(16).padStart(6, '0')}` }}
            />
            <span className="text-zinc-600 text-xs">{cell?.name || selectedCellId}</span>
          </div>
        </div>
      )}
    </div>
  );
};
